import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { I18nProvider } from "@lingui/react";
import { i18n, activateLocale, getDefaultLocale } from "@/i18n.ts";
import type { Locale } from "@/i18n";

type LanguageContextValue = {
  locale: Locale;
  isLoading: boolean;
  setLocale: (locale: Locale) => Promise<void>;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const LanguageProviderFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F5FAFF] text-[#166B93] font-semibold">
    Loading...
  </div>
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      try {
        const detected = getDefaultLocale();
        await activateLocale(detected);
        if (isMounted) {
          setLocaleState(detected);
        }
      } catch (error) {
        console.error("Failed to initialize locale", error);
        if (isMounted) {
          setLocaleState("en");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLocaleChange = useCallback(
    async (nextLocale: Locale) => {
      if (nextLocale === locale) {
        return;
      }
      setIsLoading(true);
      try {
        await activateLocale(nextLocale);
        setLocaleState(nextLocale);
      } finally {
        setIsLoading(false);
      }
    },
    [locale]
  );

  const value = useMemo<LanguageContextValue | null>(() => {
    if (!locale) {
      return null;
    }
    return {
      locale,
      isLoading,
      setLocale: handleLocaleChange,
    };
  }, [handleLocaleChange, isLoading, locale]);

  if (!value) {
    return <LanguageProviderFallback />;
  }

  return (
    <LanguageContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

